<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import { storeToRefs } from 'pinia';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Calendar from 'primevue/calendar';
import { usePaymentStore, useOrganizationStore } from '@/store';
import { useCurrency, useDateTime } from '@/composables';

const expandedRows = ref();
const paymentStore = usePaymentStore();
const organizationStore = useOrganizationStore();
const { payments, start_at, end_at } = storeToRefs(paymentStore);
const { organizationsIdsSelected } = storeToRefs(organizationStore);

const getPayments = async () => {
  if (!datesInverted.value) await paymentStore.getCurrentUserPayments(organizationsIdsSelected.value);
};

onMounted(getPayments);

watch([start_at, end_at, organizationsIdsSelected], getPayments);

const datePeriodText = (start: string, end: string) => {
  const str = useDateTime().checkProperIntlFormatting(start);

  const dtf = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });

  const startPeriod = dtf.format(Date.parse(str));
  const endPeriod = new Date(end);

  return `${startPeriod} - ${useDateTime().formatDateShortMonth(endPeriod.toString())}`;
};

const datesInverted = computed(() => {
  return start_at.value && end_at.value ? start_at.value >= end_at.value : false;
});

/**
 * make the selected end date the last day of that month
 * @param value = end date string
 */
const updateEndDate = (value: string) => {
  end_at.value = new Date(useDateTime().getEndOfMonthDate(value, 1));
};
</script>

<template>
  <div class="w-full">
    <Card>
      <template #content>
        <div class="grid p-2">
          <div class="col-12 flex justify-content-start align-content-center">
            <div class="flex relative gap-1 align-items-center justify-content-start">
              <Calendar v-model="start_at" view="month" class="w-9rem text-sm" date-format="MM yy" />
              to
              <Calendar
                v-model="end_at"
                view="month"
                class="w-9rem text-sm"
                date-format="MM yy"
                @date-select="updateEndDate"
              />
            </div>
            <div class="note flex align-items-center ml-4 font-normal">
              <strong>Note:</strong>
              &nbsp;This payment data does not include manual adjustment.
            </div>
          </div>
        </div>
        <DataTable
          v-model:expandedRows="expandedRows"
          filter-display="menu"
          :value="payments"
          :total-records="paymentStore.total"
          data-key="bucket_start_at"
          :row-hover="true"
          :lazy="true"
          loading-icon="logo-spinner"
          :loading="paymentStore.loading"
          responsive-layout="scroll"
        >
          <template #empty>No Payment Found</template>
          <template #loading>Loading data. Please wait.</template>
          <Column field="" :expander="true" header-class="no-center" header-style="width: 20px" />
          <Column field="period" header="Payment Period" header-class="no-center col-2">
            <template #body="{ data }">{{ datePeriodText(data.bucket_start_at, data.bucket_end_at) }}</template>
          </Column>
          <Column field="type" header="Type" body-class="text-center capitalize">
            <template #body="{ data }">{{ data.events[0]?.payment_type || 'N/A' }}</template>
          </Column>
          <Column field="hours" header="Hours" body-class="text-center">
            <template #body="{ data }">
              {{ data.hours.toFixed(2) }}
            </template>
          </Column>
          <Column field="total" header="Total" body-class="text-center">
            <template #body="{ data }">{{ useCurrency().formatCurrency(data.total) }}</template>
          </Column>
          <Column field="count" header="Events" body-class="text-center" />
          <template #expansion="slotProps">
            <div class="payment-details">
              <div>
                <div class="grid">
                  <div class="col-12">
                    <DataTable
                      :value="slotProps.data.events"
                      data-key="event_id"
                      class="px-3"
                      :row-hover="true"
                      :lazy="true"
                      responsive-layout="scroll"
                    >
                      <template #empty>No Events Found</template>
                      <Column field="start_at" header="Date" header-class="no-center details-table-header py-0">
                        <template #body="{ data }">
                          {{ useDateTime().formatDateShortMonth(data.start_date) }}
                        </template>
                      </Column>
                      <Column
                        field="time"
                        header="Time"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      >
                        <template #body="{ data }">
                          {{ `${data.start_time} - ${data.end_time}` }}
                        </template>
                      </Column>
                      <Column
                        field="completed_at"
                        header="Documented"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      >
                        <template #body="{ data }">
                          {{ data.completed_at }}
                        </template>
                      </Column>
                      <Column
                        field="organization_name"
                        header="Customer"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      />
                      <Column
                        field="event_type"
                        header="Event Type"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      />
                      <Column
                        field="duration"
                        header="Duration"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      />
                      <Column
                        field="rate"
                        header="Rate"
                        body-class="text-center"
                        header-class="details-table-header py-0"
                      >
                        <template #body="{ data }">{{ useCurrency().formatCurrency(data.rate) }}</template>
                      </Column>
                    </DataTable>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style scoped lang="scss">
:deep(.p-card-body) {
  .note {
    font-size: 10px;
    line-height: 12px;
    color: $grayDark;
  }
  .p-datatable {
    font-size: 14px;
    line-height: 18px;

    .p-column-header-content {
      justify-content: center;

      .p-datatable-header {
        background: $white;
        padding: 0.5rem;
      }
    }
    .payment-details {
      .details-table-header {
        height: 32px;
      }
    }
    .no-center {
      .p-column-header-content {
        justify-content: start;
      }
    }
    .p-datatable-wrapper {
      margin: 0px !important;
    }

    .p-paginator-current {
      flex: 1;
      justify-content: flex-end;
    }
  }
}
</style>
